import { computed, inject, Service, signal } from '@angular/core';
import type { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs/esm';

const toNativeDate = (struct: Nullable<NgbDateStruct>): Nullable<Date> => {
  if (
    !struct ||
    !Number.isInteger(struct.year) ||
    !Number.isInteger(struct.month) ||
    !Number.isInteger(struct.day)
  ) {
    return null;
  }

  const date = new Date(struct.year, struct.month - 1, struct.day, 12);

  date.setFullYear(struct.year);

  return date;
};

const toDateStruct = (date: Date): Nullable<NgbDateStruct> =>
  isNaN(date.getTime())
    ? null
    : {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      };

@Service()
export class DateFormatService {
  translate = inject(TranslateService);

  format = signal<string>('');
  dateFormatter = signal<string>('');

  dateTimeFormat = computed(() => `${this.format()} HH:mm`);
  dateTimeFormatter = computed(() => `${this.dateFormatter()} HH:mm`);

  constructor() {
    this.updateDateFormatter();

    this.translate.onLangChange.subscribe(() => {
      this.updateDateFormatter();
    });
  }

  updateDateFormatter = () => {
    this.format.set(this.translate.instant('WEB_I18N_DATE'));
    this.dateFormatter.set(this.format().replace(/D/g, 'd').replace(/Y/g, 'y'));
  };

  modelToDate = (model: string): Nullable<Date> =>
    toNativeDate(this.parse(model));

  dateToStruct = (date: Date) => toDateStruct(date);

  dateToModel = (date: Date) => {
    const struct = toDateStruct(date);

    return struct ? this.toModel(struct) : null;
  };

  timestampToModel = (timestamp: number) =>
    this.dateToModel(new Date(timestamp));

  parse = (value: string): Nullable<NgbDateStruct> => {
    if (value) {
      const dayjsDate = dayjs(value, this.format());

      if (!dayjsDate.isValid()) {
        return null;
      }

      return {
        year: dayjsDate.year(),
        month: dayjsDate.month() + 1,
        day: dayjsDate.date(),
      };
    }

    return null;
  };

  toModel = (date: NgbDateStruct) => {
    if (!date) {
      return '';
    }

    const dayjsDate = dayjs()
      .year(date.year)
      .month(date.month - 1)
      .date(date.day);

    return dayjsDate.isValid() ? dayjsDate.format(this.format()) : '';
  };

  timestampToTimeStruct = (timestamp: number): NgbTimeStruct => {
    const dayjsDate = dayjs(timestamp);

    return {
      hour: dayjsDate.hour(),
      minute: dayjsDate.minute(),
      second: dayjsDate.second(),
    };
  };

  dateToTimeStruct = (date: Date): NgbTimeStruct => {
    const dayjsDate = dayjs(date);

    return {
      hour: dayjsDate.hour(),
      minute: dayjsDate.minute(),
      second: dayjsDate.second(),
    };
  };

  combineDateAndTime = (dateString: string, timeStruct: NgbTimeStruct) => {
    if (!dateString || !timeStruct) {
      return null;
    }

    const date = this.modelToDate(dateString);

    if (!date) {
      return null;
    }

    const dateStruct = this.dateToStruct(date);

    if (!dateStruct) {
      return null;
    }

    return new Date(
      dateStruct.year,
      dateStruct.month - 1,
      dateStruct.day,
      timeStruct.hour,
      timeStruct.minute,
      0,
      0
    );
  };
}
